import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Tab, TabSeparator } from '../ui-v2.5';
import { UsuarioContext } from '../../providers/personaContext';

/**
 * Barra de pestañas COMPARTIDA del módulo Incorporaciones (§3.4). Única fuente de la barra en las
 * cuatro pantallas del módulo (Incorporaciones · Bajas · Áreas · Recursos), para que el orden y el
 * gate no se dupliquen ni deriven entre ficheros.
 *
 * Orden (mockup): Incorporaciones · Bajas ┃ Áreas · Recursos · Puestos · Perfiles · Avisos · Documentos
 *   - Incorporaciones y Bajas: SIEMPRE visibles (permiso .ver ya lo exige la ruta).
 *   - Grupo de configuración (tras el separador): SOLO con incorporaciones.configurar (o Super Admin).
 *     Áreas y Recursos NAVEGAN; Puestos/Perfiles/Avisos/Documentos deshabilitadas con Tooltip hasta
 *     que tengan pantalla en fases posteriores.
 *
 * `active` = pestaña actual ('incorporaciones' | 'bajas' | 'areas' | 'recursos'). El gate de cliente
 * replica el patrón can(clave) de MenuPersonas/SoporteSidebar; la barrera real es el backend.
 */

const TOOLTIP_FASE = 'Disponible en próximas fases';
const BASE = '/gestionpersonas/personas';
const RUTAS = {
  incorporaciones: `${BASE}/incorporaciones`,
  bajas: `${BASE}/bajas`,
  areas: `${BASE}/incorporaciones/areas`,
  // `cuentas` se fue a Personas › Buzones y listas (30/10): la ruta vieja redirige allí desde App.js.
  recursos: `${BASE}/incorporaciones/recursos`,
  puestos: `${BASE}/incorporaciones/puestos`,
  motivos: `${BASE}/incorporaciones/motivos-baja`,
  tareas: `${BASE}/incorporaciones/tareas-baja`,
  perfiles: `${BASE}/incorporaciones/perfiles`,
  avisos: `${BASE}/incorporaciones/avisos`,
};

// Grupo de configuración (tras el separador). Solo Documentos queda en espera (fase posterior).
const CONFIG_TABS = [
  { value: 'areas', label: 'Áreas' },
  { value: 'recursos', label: 'Recursos' },
  { value: 'puestos', label: 'Puestos' },
  { value: 'motivos', label: 'Motivos de baja' },
  { value: 'tareas', label: 'Tareas de baja' },
  { value: 'perfiles', label: 'Perfiles' },
  { value: 'avisos', label: 'Avisos' },
  { value: 'documentos', label: 'Documentos', disabled: true },
];

export default function IncorporacionesTabs({ active }) {
  const navigate = useNavigate();
  const ctx = useContext(UsuarioContext) || {};
  const isSuperAdmin = (ctx.persona?.rol || '').toLowerCase() === 'super administrador';
  const permisos = Array.isArray(ctx.permisos) ? ctx.permisos : [];
  const puedeConfigurar = isSuperAdmin || permisos.includes('incorporaciones.configurar');
  const moduloActivo = (ctx.modulos || {}).incorporaciones === true;

  /* ── CON EL MÓDULO APAGADO, ESTA BARRA NO SE PINTA ──────────────────────────────────────
   *
   * Es la barra COMPARTIDA de las cuatro pantallas, y `Bajas` —la única que se queda— también la
   * pintaba. Dejarla intacta habría dejado en pie seis pestañas hacia pantallas que ya no
   * existen:
   *
   *   **Ocultar algo incluye ocultar lo que lleva a ello.** Tapar el módulo y dejar su barra de
   *   navegación es PEOR que no taparlo: el 404 pasa de imposible de alcanzar a estar a un clic.
   *
   * Y no se deja sólo la pestaña «Bajas»: una barra de una pestaña, que además es la página en
   * la que ya estás, no lleva a ninguna parte. `PageHeader` ya da título y subtítulo, así que la
   * pantalla no queda descabezada.
   */
  if (!moduloActivo) return null;

  const go = (value) => {
    const destino = RUTAS[value];
    if (destino && value !== active) navigate(destino);
  };

  return (
    <Tabs value={active} onChange={go}>
      <Tab value="incorporaciones" label="Incorporaciones" />
      <Tab value="bajas" label="Bajas" />
      {puedeConfigurar && <TabSeparator />}
      {puedeConfigurar &&
        CONFIG_TABS.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={t.label}
            disabled={t.disabled}
            tooltip={t.disabled ? TOOLTIP_FASE : undefined}
          />
        ))}
    </Tabs>
  );
}
